import * as React from "react";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  rectSortingStrategy,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

type SortableListProps = {
  /** The rows' sortable ids, in display order; each row calls useSortable with its own. */
  ids: string[];
  /** A row dropped from position `from` onto the row at `to`. */
  onReorder: (from: number, to: number) => void;
  children: React.ReactNode;
  /** Items wrap in rows (chips) and move freely; otherwise a vertical list. */
  wrap?: boolean;
};

/**
 * A vertical drag-to-reorder list: a pointer drag starts after 4px, so a click on the
 * handle stays a click, and the row only moves up and down.
 */
export function SortableList({ ids, onReorder, children, wrap }: SortableListProps) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from !== -1 && to !== -1) onReorder(from, to);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={wrap ? [] : [restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={ids}
        strategy={wrap ? rectSortingStrategy : verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
}
